import { useState, type CSSProperties } from 'react';
import { Plus, Trash2, Save, Check, AlertCircle, Archive, RotateCcw } from 'lucide-react';
import { Eyebrow } from '../components/ui';
import { api, isSampleMode } from '../api/client';
import type { CustomDimensionDefView, OrgBlend, OrgDimensionSetView } from '../types';

// Mirrors backend agent/scoring-config.json customDimensions.maxCount / maxRubricChars.
const MAX_ACTIVE = 5;
const MAX_RUBRIC = 600;

type Row = CustomDimensionDefView & { _published: boolean };

const input: CSSProperties = { width: '100%', fontSize: 13, padding: '8px 10px', borderRadius: 6, border: '1px solid #E2E2E6', boxSizing: 'border-box', fontFamily: 'inherit' };
const label: CSSProperties = { fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#75757F', marginBottom: 5, display: 'block' };

/** Pull the server's human message out of a `"<status> {json}"` error thrown by the api client. */
function friendlyError(e: unknown): string {
  const msg = (e as Error).message ?? 'Could not publish.';
  const m = msg.match(/\{[\s\S]*\}/);
  if (m) {
    try {
      const parsed = JSON.parse(m[0]) as { error?: { message?: string } };
      if (parsed.error?.message) return parsed.error.message;
    } catch { /* fall through */ }
  }
  return msg;
}

export function CustomDimensionsEditor({ campaignId, initial, onPublished }: {
  campaignId: string;
  initial?: OrgDimensionSetView;
  onPublished?: (set: OrgDimensionSetView) => void;
}) {
  const [rows, setRows] = useState<Row[]>(() => (initial?.dimensions ?? []).map((d) => ({ ...d, _published: true })));
  const [blend, setBlend] = useState<OrgBlend>(initial?.blend ?? { mode: 'sidecar' });
  const [version, setVersion] = useState(initial?.version ?? 0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const dirty = () => { setSaved(false); setError(null); };
  const update = (i: number, patch: Partial<Row>) => { dirty(); setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r))); };
  const addRow = () => { dirty(); setRows((rs) => [...rs, { label: '', rubric: '', weight: 0.5, _published: false }]); };
  const removeRow = (i: number) => { dirty(); setRows((rs) => rs.filter((_, j) => j !== i)); };

  const active = rows.filter((r) => !r.retired);
  const sumActive = active.reduce((s, r) => s + (Number(r.weight) || 0), 0);
  const activeCount = active.length;
  const normalisedPct = (r: Row) => (r.retired || sumActive <= 0 ? 0 : Math.round(((Number(r.weight) || 0) / sumActive) * 100));

  const publish = async () => {
    setBusy(true); setError(null); setSaved(false);
    const body = { dimensions: rows.map(({ _published, ...d }) => d), blend };
    try {
      let set: OrgDimensionSetView;
      if (isSampleMode) {
        set = { version: version + 1, dimensions: body.dimensions, blend, updatedAt: new Date().toISOString() };
      } else {
        ({ customDimensions: set } = await api.publishDimensions(campaignId, body));
      }
      setRows(set.dimensions.map((d) => ({ ...d, _published: true })));
      setBlend(set.blend);
      setVersion(set.version);
      setSaved(true);
      onPublished?.(set);
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-panel rounded-card" style={{ padding: 24, marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 4 }}>
        <div>
          <Eyebrow>Organisation-specific dimensions</Eyebrow>
          <div className="text-storm" style={{ fontSize: 13, marginTop: 3 }}>
            Supplement the universal model (the five criteria and 4D) with expectations specific to your organisation, such as a policy or a way of working. Avery assesses these alongside it, without changing the universal band. {version > 0 ? `Published v${version}.` : 'Not yet published.'}
          </div>
        </div>
        <span className="text-storm" style={{ fontSize: 12 }}>{activeCount}/{MAX_ACTIVE} active</span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, margin: '16px 0' }}>
        {rows.length === 0 && (
          <div className="text-storm" style={{ fontSize: 13, padding: '14px 0' }}>No dimensions yet. Add one to assess an organisation-specific expectation.</div>
        )}
        {rows.map((r, i) => (
          <div key={r.key ?? `new-${i}`} className="border border-panel" style={{ borderRadius: 8, padding: 14, opacity: r.retired ? 0.6 : 1 }}>
            <div style={{ display: 'flex', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
              <div style={{ flex: '2 1 240px' }}>
                <label style={label}>Label</label>
                <input style={input} value={r.label} placeholder="e.g. Uses our approved model gateway" onChange={(e) => update(i, { label: e.target.value })} disabled={r.retired} />
              </div>
              <div style={{ flex: '0 0 130px' }}>
                <label style={label}>Weight ({normalisedPct(r)}%)</label>
                <input style={input} type="number" min={0} max={1} step={0.05} value={r.weight} onChange={(e) => update(i, { weight: Number(e.target.value) })} disabled={r.retired} />
              </div>
            </div>
            <div style={{ marginBottom: 10 }}>
              <label style={label}>Rubric — what Avery looks for ({(r.rubric ?? '').length}/{MAX_RUBRIC})</label>
              <textarea style={{ ...input, minHeight: 56, resize: 'vertical' }} maxLength={MAX_RUBRIC} value={r.rubric} placeholder="e.g. Routes prompts through the sanctioned gateway and can say why." onChange={(e) => update(i, { rubric: e.target.value })} disabled={r.retired} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              <label className="text-storm" style={{ fontSize: 12.5, display: 'inline-flex', alignItems: 'center', gap: 6, cursor: r.retired ? 'default' : 'pointer' }}>
                <input type="checkbox" checked={!!r.informationalOnly} disabled={r.retired} onChange={(e) => update(i, { informationalOnly: e.target.checked })} />
                Informational only (not scored into a band)
              </label>
              <div style={{ marginLeft: 'auto', display: 'inline-flex', gap: 8 }}>
                {r._published ? (
                  <button type="button" onClick={() => update(i, { retired: !r.retired })} className="border border-panel text-storm" style={{ background: 'transparent', fontSize: 12, fontWeight: 600, borderRadius: 6, padding: '6px 10px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    {r.retired ? <><RotateCcw size={13} /> Restore</> : <><Archive size={13} /> Retire</>}
                  </button>
                ) : (
                  <button type="button" onClick={() => removeRow(i)} className="border border-panel" style={{ background: 'transparent', color: '#C0392B', fontSize: 12, fontWeight: 600, borderRadius: 6, padding: '6px 10px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <Trash2 size={13} /> Remove
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <button type="button" onClick={addRow} disabled={activeCount >= MAX_ACTIVE} className="border border-panel text-code" style={{ background: 'transparent', fontSize: 13, fontWeight: 600, borderRadius: 6, padding: '8px 12px', cursor: activeCount >= MAX_ACTIVE ? 'not-allowed' : 'pointer', opacity: activeCount >= MAX_ACTIVE ? 0.5 : 1, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <Plus size={14} /> Add dimension
      </button>

      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginTop: 18, paddingTop: 16, borderTop: '1px solid #EFEFF2' }}>
        <label className="text-storm" style={{ fontSize: 12.5, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          Scoring
          <select value={blend.mode} onChange={(e) => { dirty(); setBlend(e.target.value === 'blended' ? { mode: 'blended', groupWeight: 0.2 } : { mode: 'sidecar' }); }} style={{ ...input, width: 'auto', padding: '6px 8px' }}>
            <option value="sidecar">Sidecar (separate, recommended)</option>
            <option value="blended">Blended into a second band</option>
          </select>
        </label>
        {blend.mode === 'blended' && (
          <label className="text-storm" style={{ fontSize: 12.5, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            Org weight
            <input type="number" min={0} max={1} step={0.05} value={blend.groupWeight} onChange={(e) => { dirty(); setBlend({ mode: 'blended', groupWeight: Number(e.target.value) }); }} style={{ ...input, width: 80, padding: '6px 8px' }} />
          </label>
        )}
        <div style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 12 }}>
          {error && <span style={{ fontSize: 12.5, color: '#C0392B', display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: 420 }}><AlertCircle size={14} style={{ flex: 'none' }} /> {error}</span>}
          {saved && !error && <span className="text-green" style={{ fontSize: 12.5, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}><Check size={14} /> Published v{version}</span>}
          <button type="button" onClick={publish} disabled={busy} className="bg-code text-white" style={{ border: 'none', fontSize: 13, fontWeight: 700, borderRadius: 6, padding: '9px 16px', cursor: busy ? 'wait' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 7 }}>
            <Save size={14} /> {busy ? 'Publishing…' : 'Publish'}
          </button>
        </div>
      </div>
    </div>
  );
}
